const BARE_BASE64_CHARSET = /^[A-Za-z0-9+/=\s]+$/

function stripLeadingBom(str) {
  return str.replace(/^\uFEFF/, '')
}

function decodeUrlEncodedMarkupIfNeeded(t) {
  if (t.startsWith('%3C') || t.startsWith('%3c')) {
    try {
      return decodeURIComponent(t)
    } catch {
      return t
    }
  }
  return t
}

function sniffBinaryImageDataUrl(cleanBase64) {
  try {
    const capped = Math.min(cleanBase64.length, 120)
    const alignedLen = capped - (capped % 4)
    const slice = cleanBase64.slice(0, Math.max(alignedLen, 12))
    const bin = atob(slice)
    if (bin.length < 3) {
      return null
    }
    const c0 = bin.charCodeAt(0)
    const c1 = bin.charCodeAt(1)
    const c2 = bin.charCodeAt(2)
    if (c0 === 0x89 && c1 === 0x50 && c2 === 0x4e) {
      return `data:image/png;base64,${cleanBase64}`
    }
    if (c0 === 0xff && c1 === 0xd8 && c2 === 0xff) {
      return `data:image/jpeg;base64,${cleanBase64}`
    }
    if (c0 === 0x47 && c1 === 0x49 && c2 === 0x46) {
      return `data:image/gif;base64,${cleanBase64}`
    }
    if (c0 === 0x52 && c1 === 0x49 && c2 === 0x46) {
      return `data:image/webp;base64,${cleanBase64}`
    }
  } catch {
    return null
  }
  return null
}

function looksLikeBareBase64(t) {
  const clean = t.replace(/\s/g, '')
  if (clean.length < 80) {
    return false
  }
  return BARE_BASE64_CHARSET.test(clean)
}

/**
 * Tenant branding `logo_light` is sometimes stored as raw SVG markup. Assigning that string to
 * `Image#src` makes the browser treat it as a path under the app origin (e.g. GET /%3Csvg%20...),
 * which fails (414 / broken PDF). Normalize to a proper data URL before loading.
 *
 * Bare base64 (no `data:` prefix) is also treated as a relative URL and triggers the same class of failure.
 *
 * @param {string} s
 * @returns {string}
 */
export function normalizeBrandingImageSrc(s) {
  if (s == null || typeof s !== 'string') {
    return s
  }
  const t = stripLeadingBom(s.trim())
  if (t.startsWith('data:') || /^https?:\/\//i.test(t) || t.startsWith('/')) {
    return t
  }
  let markup = decodeUrlEncodedMarkupIfNeeded(t)
  markup = stripLeadingBom(markup.trim())
  if (/^\s*(?:<\?xml[^>]*>\s*)?<svg\b/i.test(markup)) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
  }
  if (looksLikeBareBase64(t)) {
    const clean = t.replace(/\s/g, '')
    const sniffed = sniffBinaryImageDataUrl(clean)
    if (sniffed) {
      return sniffed
    }
    return `data:image/png;base64,${clean}`
  }
  return t
}

const DEFAULT_LOGO = '/media/logos/logo-dark.svg'

const MAX_RASTER_DIMENSION = 2048

/** 1×1 PNG — jsPDF-safe last resort if rasterization fails */
const FALLBACK_JSPDF_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='

function isJsPdfSafePngDataUrl(s) {
  return typeof s === 'string' && /^data:image\/png/i.test(s) && /;base64,/i.test(s) && s.length > 50
}

/** So drawing to canvas + `toDataURL()` does not taint when the server sends CORS headers. */
function configureImageCrossOrigin(img, src) {
  if (typeof src === 'string' && /^https?:\/\//i.test(src)) {
    img.crossOrigin = 'anonymous' // eslint-disable-line no-param-reassign
  }
}

function loadImageFromSrc(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    configureImageCrossOrigin(img, src)
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Branding image failed to load'))
    img.src = src
  })
}

function drawImageToPngDataUrl(img) {
  const nw = img.naturalWidth || img.width
  const nh = img.naturalHeight || img.height
  if (!nw || !nh) {
    throw new Error('Invalid image dimensions')
  }
  let rw = nw
  let rh = nh
  if (rw > MAX_RASTER_DIMENSION || rh > MAX_RASTER_DIMENSION) {
    const scale = Math.min(MAX_RASTER_DIMENSION / rw, MAX_RASTER_DIMENSION / rh)
    rw = Math.max(1, Math.round(rw * scale))
    rh = Math.max(1, Math.round(rh * scale))
  }
  const canvas = document.createElement('canvas')
  canvas.width = rw
  canvas.height = rh
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas unsupported')
  }
  ctx.drawImage(img, 0, 0, rw, rh)
  return canvas.toDataURL('image/png')
}

/**
 * jsPDF `addImage(..., 'PNG', …)` only accepts PNG (or a matching format). SVG-backed
 * `HTMLImageElement`, WebP/JPEG/GIF data URLs, etc. are reported as type UNKNOWN unless rasterized.
 *
 * @param {string|HTMLImageElement} logo
 * @param {string} fallbackSrc
 * @returns {Promise<string>} PNG data URL
 */
function rasterizeBrandingForJsPdf(logo, fallbackSrc) {
  const fallbackToPng = () => {
    const fbSrc = normalizeBrandingImageSrc(fallbackSrc) || fallbackSrc
    return loadImageFromSrc(fbSrc)
      .then(drawImageToPngDataUrl)
      .catch(() => FALLBACK_JSPDF_PNG)
  }

  // Always decode via Image + canvas so output is an 8-bit RGBA PNG jsPDF accepts. Some tenant
  // `data:image/png;base64,...` blobs parse in browsers but trip jsPDF’s sniffer as type UNKNOWN.
  if (typeof logo === 'string') {
    return loadImageFromSrc(logo)
      .then(drawImageToPngDataUrl)
      .catch(() => fallbackToPng())
  }

  if (logo instanceof HTMLImageElement) {
    const imageElement = logo
    if (imageElement.complete) {
      if (imageElement.naturalWidth > 0) {
        try {
          return Promise.resolve(drawImageToPngDataUrl(imageElement))
        } catch {
          return fallbackToPng()
        }
      }
      return fallbackToPng()
    }

    return new Promise((resolve, reject) => {
      imageElement.onload = () => {
        if (imageElement.naturalWidth > 0) {
          try {
            resolve(drawImageToPngDataUrl(imageElement))
            return
          } catch {
            /* fall through */
          }
        }
        fallbackToPng().then(resolve, reject)
      }
      imageElement.onerror = () => {
        fallbackToPng().then(resolve, reject)
      }
    })
  }

  return fallbackToPng()
}

function loadBrandingImageRaw(url, fallbackSrc = DEFAULT_LOGO) {
  return new Promise((resolve) => {
    if (url == null || url === '') {
      const fb = new Image()
      configureImageCrossOrigin(fb, fallbackSrc)
      fb.onload = () => resolve(fb)
      fb.src = fallbackSrc
      return
    }
    const src = normalizeBrandingImageSrc(url)
    // jsPDF handles raster data URLs as strings; UTF-8 / URI-encoded SVG is loaded via Image for sizing.
    if (
      typeof src === 'string' &&
      /^data:image\//i.test(src) &&
      !(/^data:image\/svg\+xml/i.test(src) && !/;base64,/i.test(src))
    ) {
      resolve(src)
      return
    }
    const img = new Image()
    configureImageCrossOrigin(img, src)
    img.onload = () => resolve(img)
    img.onerror = () => {
      const fb = new Image()
      configureImageCrossOrigin(fb, fallbackSrc)
      fb.onload = () => resolve(fb)
      fb.src = fallbackSrc
    }
    img.src = src
  })
}

/**
 * @param {string} [url] Branding URL, data URL, path, or raw / URL-encoded SVG markup
 * @param {string} [fallbackSrc]
 * @returns {Promise<string>} PNG data URL for jsPDF.getImageProperties / addImage(..., 'PNG', …)
 */
export function loadBrandingImageForPdf(url, fallbackSrc = DEFAULT_LOGO) {
  return loadBrandingImageRaw(url, fallbackSrc)
    .then((logo) => rasterizeBrandingForJsPdf(logo, fallbackSrc))
    .then((png) => (isJsPdfSafePngDataUrl(png) ? png : FALLBACK_JSPDF_PNG))
    .catch(() => FALLBACK_JSPDF_PNG)
}

/**
 * @param {object} [brandingData] The organization's branding settings (Redux `branding.data`)
 * @returns {string|undefined} The light logo when the organization publishes it on reports
 */
export const getPublishedBrandingLogo = (brandingData) =>
  brandingData?.logo_light && brandingData?.publish_logo ? brandingData.logo_light : undefined
